import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../services/api";
import cdrFilesService from '../../services/cdrFilesService';
import OrdersFilters from "./LeftPanel/OrdersFilters";
import OrdersListPositions from "./LeftPanel/OrdersListPositions";
import OrdersListSupplies from "./LeftPanel/OrdersListSupplies";
import LayoutingSheetsPanel from "./RightPanel/LayoutingSheetsPanel";
import DownloadProgressBar from "./common/DownloadProgressBar";
import CdrFilesServerWarning from '../Common/CdrFilesServerWarning';
import NestingModal from "./common/NestingModal";

export default function LayoutingPage() {
  // ---------- ЛЕВАЯ ПАНЕЛЬ ----------
  const navigate = useNavigate();
  const [showNestingModal, setShowNestingModal] = useState(false);
  
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    namePart: "",
    service: null,
    categoryIds: [],
    sortParam: "time_desc",
    hasVarnish: null,
    hasQrCodes: null,
  });

  const [cdrFilesServerAvailable, setCdrFilesServerAvailable] = useState(false);

  const [orderPositions, setOrderPositions] = useState([]);
  const [itemsCdrFiles, setItemsCdrfiles] = useState([]);
  const [selectedOrderPositionIds, setSelectedOrderPositionIds] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({});
  
  const [leftViewMode, setLeftViewMode] = useState("supplies"); // positions | supplies

  // кнопка "Скомпоновать таблички"
  const [isCombiningTables, setIsCombiningTables] = useState(false);
  const [isNesting, setIsNesting] = useState(false);
  const [nestingError, setNestingError] = useState(null);

  // =============================================================
  //                      ЗАГРУЗКА ЛЕВОЙ ПАНЕЛИ
  // =============================================================
  const fetchCategories = async () => {
    try {
      const [categoriesRes] = await Promise.all([
        api.get('/categories'),
      ]);
      setCategories(categoriesRes.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchOrderPositions = async () => {
    setOrdersLoading(true);
    try {
      const res = await api.post("/layouting/orders", filters);
      const orderPositions = res.data.elements || [];
      setOrderPositions(orderPositions);
      fetchCdrfiles(orderPositions);
    } catch (err) {
      console.error("Ошибка загрузки позиций:", err);
    }
    setOrdersLoading(false);
  };

   // Функция для загрузки cdrfile по itemId
  const fetchCdrfiles = async (orderPositions) => {
    if (!orderPositions) return null;
    try {
      const itemIds = orderPositions.map(op => op.itemId);
      const payload = { itemIds: itemIds};
      const response = await cdrFilesService.post(`/cdrfiles`, payload);
      
      const cdrfiles = response.data.elements;

      const itemIds_cdrfiles = {};
      for (const cdrfile of cdrfiles) {
        if (cdrfile) {
          itemIds_cdrfiles[cdrfile.itemId] = cdrfile;
        }
      }
      setItemsCdrfiles(itemIds_cdrfiles);
    } catch (err) {
      console.error('Error loading image:', err);
      return null;
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchOrderPositions();
  }, [filters]);

  useEffect(() => {
      const checkCdrFilesServerAvailable = async () => {
        try {
          const response = await cdrFilesService.health();
          setCdrFilesServerAvailable(true);
        } catch (err) {
          console.error(err);
          setCdrFilesServerAvailable(false);
        }
      };
      checkCdrFilesServerAvailable();
  }, []);

  // =============================================================
  // ГРУППИРОВКА ВЫБРАННЫХ ЗАКАЗОВ ПО КАТЕГОРИЯМ И ITEM ID
  // =============================================================
  const groupSelectedOrdersByCategoryAndItem = () => {
    // Получаем только выбранные позиции
    const selectedPositions = orderPositions.filter(p => 
      selectedOrderPositionIds.includes(p.id)
    );

    // Группируем данные
    const groupedData = {};

    selectedPositions.forEach(position => {
      const categoryId = position.categoryId;
      const itemId = position.itemId;
      const orderId = position.id;

      // Инициализируем категорию, если её нет
      if (!groupedData[categoryId]) {
        groupedData[categoryId] = {};
      }

      // Инициализируем itemId, если его нет
      if (!groupedData[categoryId][itemId]) {
        groupedData[categoryId][itemId] = [];
      }

      // Добавляем orderId в массив
      groupedData[categoryId][itemId].push(orderId);
    });

    return groupedData;
  };

  // =============================================================
  //              СКАЧАТЬ .CDR ДЛЯ ВЫБРАННЫХ ПОЗИЦИЙ
  // =============================================================
  const downloadSelectedOrdersCdr = async () => {
    if (selectedOrderPositionIds.length === 0) return;

    // Группируем выбранные заказы
    const groupedPayload = groupSelectedOrdersByCategoryAndItem();

    const key = "ordersCdr";
    setDownloadProgress((p) => ({ ...p, [key]: { loaded: 0, total: 1 } }));

    try {
      const res = await cdrFilesService.post(
        "/orders/cdrfiles/download",
        { CategoryIds_ItemIds_OrderIds: groupedPayload }, // Отправляем сгруппированные данные
        {
          responseType: "blob",
          onDownloadProgress: (e) => {
            setDownloadProgress((prev) => ({
              ...prev,
              [key]: { loaded: e.loaded, total: e.total || 1 },
            }));
          },
        }
      );

      // Имя файла
      const cdHeader = res.headers["content-disposition"];
      let fileName = "orders.cdr";
      if (cdHeader) {
        const match = cdHeader.match(/filename="?(.+)"?/);
        if (match) fileName = match[1];
      }

      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();

    } catch (err) {
      console.error("Ошибка скачивания:", err);
    }

    setDownloadProgress((p) => ({ ...p, [key]: null }));
  };

  // =============================================================
  //           ГРУППИРОВКА ПО ПОСТАВКАМ (для supplies режима)
  // =============================================================
  const groupedBySupply = useMemo(() => {
    const map = new Map();

    for (const p of orderPositions) {
      const supplyObj = p.supply ?? null;
      const supplyId = supplyObj?.id || "NO_SUPPLY";

      if (!map.has(supplyId)) {
        map.set(supplyId, {
          supplyId,
          supply: supplyObj,
          positions: [],
        });
      }

      map.get(supplyId).positions.push(p);
    }

    return Array.from(map.values()).sort((a, b) => {
      if (a.supplyId === 'NO_SUPPLY') return 1;
      if (b.supplyId === 'NO_SUPPLY') return -1;

      const at = new Date(a.supply?.time || 0);
      const bt = new Date(b.supply?.time || 0);

      return bt - at;
    });
  }, [orderPositions]);

  // =============================================================
  //            ЛОГИКА ДЛЯ КОМПОНОВКИ ТАБЛИЧЕК
  // =============================================================

  // 1. Определяем категорию "Таблички"
  const tablesCategory = categories.find(c => c.name?.toLowerCase() === "таблички");
  const tablesCategoryId = tablesCategory?.id;

  // 2. Фильтруем выбранные позиции
  const selectedPositions = useMemo(
    () => orderPositions.filter(p => selectedOrderPositionIds.includes(p.id)),
    [selectedOrderPositionIds, orderPositions]
  );

  // 3. Проверяем условия для кнопки
  const canCombineTables = useMemo(() => {
    if (!tablesCategoryId) return false;
    if (selectedPositions.length < 20) return false;

    return selectedPositions.every(p => p.categoryId === tablesCategoryId);
  }, [selectedPositions, tablesCategoryId]);

  // 4. API вызов
  const handleConcatTables = async () => {

    if (selectedOrderPositionIds.length === 0) return;

    setIsCombiningTables(true);
    
    // Группируем выбранные заказы
    const groupedPayload = groupSelectedOrdersByCategoryAndItem();

    const key = "ordersCdr";
    setDownloadProgress((p) => ({ ...p, [key]: { loaded: 0, total: 1 } }));

    try {
      const res = await cdrFilesService.post(
        "/orders/cdrfiles/concat/tables",
        { CategoryIds_ItemIds_OrderIds: groupedPayload }, // Отправляем сгруппированные данные
        {
          responseType: "blob",
          onDownloadProgress: (e) => {
            setDownloadProgress((prev) => ({
              ...prev,
              [key]: { loaded: e.loaded, total: e.total || 1 },
            }));
          },
        }
      );

      // Имя файла
      const cdHeader = res.headers["content-disposition"];
      let fileName = "tables.cdr";
      if (cdHeader) {
        const match = cdHeader.match(/filename="?(.+)"?/);
        if (match) fileName = match[1];
      }

      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();

    } catch (err) {
      console.error("Ошибка компоновки табличек:", err);
      alert("Ошибка компоновки табличек. Данные могли обновиться — перезагрузите страницу.");
    } finally {
      setIsCombiningTables(false);
      setDownloadProgress((p) => ({ ...p, [key]: null }));
    }
  };

  // открытие модалки по кнопке "Скомпоновать"
const handleOpenNestingModal = () => {
  if (selectedOrderPositionIds.length === 0) return;
  if (!filters.service) {
    alert("Пожалуйста, выберите сервис в фильтрах");
    return;
  }
  setNestingError(null);
  setShowNestingModal(true);
};

// отправка из модалки
const handleNestingSubmit = async (modalValues) => {
  setIsNesting(true);
  setNestingError(null);

  const groupedPayload = groupSelectedOrdersByCategoryAndItem();

  try {
    await cdrFilesService.post(
      `/orders/cdrfiles/nesting/${filters.service}`,
      {
        CategoryIds_ItemIds_OrderIds: groupedPayload,
        ...modalValues,
      }
    );
    setShowNestingModal(false);
    navigate("/nesting");
  } catch (err) {
    console.error("Ошибка компоновки:", err);
    setNestingError(
      err.response?.data?.message ||
        "Произошла ошибка при компоновке. Попробуйте еще раз."
    );
  } finally {
    setIsNesting(false);
  }
};

  // Проверяем, можно ли активировать кнопку "Скомпоновать"
  const canNest = useMemo(() => {
    return selectedOrderPositionIds.length > 2 && filters.service;
  }, [selectedOrderPositionIds.length, filters.service]);

  // Блокировка всех кнопок при выполнении операций
  const isAnyOperationInProgress = isCombiningTables || isNesting;

  // =============================================================
  //                       UI РЕНДЕРИНГ
  // =============================================================
  return (
    <div className="container-fluid py-4">
      <div className="row">

        {/* ----------- ЛЕВАЯ ЧАСТЬ (ПОЗИЦИИ) ----------- */}
        <div className="col-6 border-end pe-4">

          <h3 className="mb-3">Позиции ожидающие компоновку</h3>

          <OrdersFilters
            filters={filters}
            setFilters={setFilters}
            categories={categories}
            viewMode={leftViewMode}
            setViewMode={setLeftViewMode}
            setSelectedOrderPositionIds={setSelectedOrderPositionIds}
          />

          <CdrFilesServerWarning cdrFilesServerAvailable={cdrFilesServerAvailable} />
      
          {/* --- УВЕДОМЛЕНИЕ ОБ ОШИБКЕ --- */}
          {nestingError && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {nestingError}
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setNestingError(null)}
                aria-label="Close"
              />
            </div>
          )}

          {/* --- КНОПКИ --- */}
          <div className="d-flex mb-3" style={{ gap: 8 }}>
            
            {/* Скачать */}
            <button 
              className="btn btn-success"
              onClick={downloadSelectedOrdersCdr}
              disabled={selectedOrderPositionIds.length === 0 || isAnyOperationInProgress || cdrFilesServerAvailable === false}
            >
              Скачать .cdr ({selectedOrderPositionIds.length})
            </button>

            {/* Скомпоновать таблички */}
            {/* <button
              className="btn btn-warning d-flex align-items-center"
              disabled={!canCombineTables || isAnyOperationInProgress}
              onClick={handleConcatTables}
            >
              {isCombiningTables && (
                <span 
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
              Скомпоновать таблички
            </button> */}

            {/* Скомпоновать */}
            <button
              className="btn btn-primary d-flex align-items-center"
              disabled={!canNest || isAnyOperationInProgress || cdrFilesServerAvailable === false}
              onClick={handleOpenNestingModal}
            >
              {isNesting && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              )}
              Скомпоновать
            </button>

            <Link to="/nesting" className="btn btn-primary d-flex align-items-center">
              ➜
            </Link>

          </div>

          {/* === ПРОГРЕСС ЗАГРУЗКИ === */}
          <DownloadProgressBar progress={downloadProgress["ordersCdr"]} />

          {leftViewMode === "positions" && (
            <OrdersListPositions
              orderPositions={orderPositions}
              itemsCdrFiles={itemsCdrFiles}
              selectedIds={selectedOrderPositionIds}
              setSelectedIds={setSelectedOrderPositionIds}
              downloadProgress={downloadProgress}
            />
          )}

          {leftViewMode === "supplies" && (
            <OrdersListSupplies
              groupedBySupply={groupedBySupply}
              itemsCdrFiles={itemsCdrFiles}
              selectedIds={selectedOrderPositionIds}
              setSelectedIds={setSelectedOrderPositionIds}
            />
          )}
        </div>

        {/* ----------- ПРАВАЯ ЧАСТЬ (ЛИСТЫ) ----------- */}
        <div className="col-6 ps-4">

          <div className="d-flex justify-content-between">
            <h3 className="mb-3">Скомпонованные листы</h3>
          </div>

          <LayoutingSheetsPanel
            fetchOrderPositions={fetchOrderPositions}
          />
        </div>
      </div>

      <NestingModal
        show={showNestingModal}
        onClose={() => setShowNestingModal(false)}
        onSubmit={handleNestingSubmit}
        isSubmitting={isNesting}
      />

    </div>
  );
}